import { proxy } from 'valtio';
import type { UIStore, NotificationInput, Notification } from '../types';

import { logger } from '@/lib/logger';
export interface NotificationService {
  createNotification: (notification: NotificationInput) => Promise<Notification>;
  scheduleRemoval: (id: string, delay: number) => () => void;
}

export interface UIActions {
  toggleSidebar: () => void;
  setActiveSection: (section: string | null) => void;
  addNotification: (notification: NotificationInput) => Promise<void>;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
  setPlanMode: (enabled: boolean) => void;
  selectPage: (pageId: string | null) => void;
  startImportProcessing: (requestId: string, fileType: string, message: string) => void;
  updateImportProgress: (message: string) => void;
  finishImportProcessing: () => void;
  requestVersionDiff: (params: { pageId: string; fromVersionId: string }) => void;
  clearVersionDiff: () => void;
}

export function createUIStore(
  services: {
    notificationService: NotificationService;
  },
  initialState?: Partial<UIStore>
) {
  const store = proxy<UIStore>({
    sidebar: {
      isOpen: true,
      activeSection: null,
    },
    selectedPageId: null,
    modals: {},
    theme: 'system',
    notifications: [],
    planMode: false,
    importProcessing: {
      isProcessing: false,
      requestId: null,
      fileType: null,
      message: '',
      startedAt: null,
    },
    pendingVersionDiff: null,
    ...initialState,
  });

  // import 自動リセット用タイムアウトのハンドル。finish 時や再 start 時に
  // clearTimeout しないとリークし、10分後に不要な store 書き込み→再レンダーが走る。
  let importTimeoutId: ReturnType<typeof setTimeout> | null = null;
  const clearImportTimeout = () => {
    if (importTimeoutId !== null) {
      clearTimeout(importTimeoutId);
      importTimeoutId = null;
    }
  };

  const actions: UIActions = {
    toggleSidebar() {
      store.sidebar.isOpen = !store.sidebar.isOpen;
    },

    setActiveSection(section: string | null) {
      store.sidebar.activeSection = section as UIStore['sidebar']['activeSection'];
    },

    async addNotification(notification: NotificationInput) {
      try {
        const created = await services.notificationService.createNotification(notification);
        store.notifications.push(created);
        
        // 自動削除をスケジュール
        if (notification.type !== 'error') {
          const _cancelRemoval = services.notificationService.scheduleRemoval(created.id, 5000);
          // 必要に応じてキャンセル関数を保持
        }
      } catch (error) {
        logger.error('Failed to create notification:', error);
        // フォールバック通知
        const fallbackNotification: Notification = {
          id: `fallback-${Date.now()}`,
          ...notification,
          timestamp: new Date(),
          read: false,
        };
        store.notifications.push(fallbackNotification);
      }
    },

    removeNotification(id: string) {
      const index = store.notifications.findIndex(n => n.id === id);
      if (index !== -1) {
        store.notifications.splice(index, 1);
      }
    },

    clearAllNotifications() {
      store.notifications = [];
    },

    setPlanMode(enabled: boolean) {
      store.planMode = enabled;
    },

    selectPage(pageId: string | null) {
      store.selectedPageId = pageId;
    },

    startImportProcessing(requestId: string, fileType: string, message: string) {
      // 多重 import で前回のタイムアウトが残らないようクリアしてから張り直す
      clearImportTimeout();
      store.importProcessing.isProcessing = true;
      store.importProcessing.requestId = requestId;
      store.importProcessing.fileType = fileType;
      store.importProcessing.message = message;
      store.importProcessing.startedAt = new Date();

      // 安全装置: 10分後に自動的にインポート処理状態をリセット（stuck state防止）
      importTimeoutId = setTimeout(() => {
        importTimeoutId = null;
        if (store.importProcessing.isProcessing &&
            store.importProcessing.requestId === requestId) {
          logger.warn('[UIStore] Auto-cleanup: Import processing timeout after 10 minutes', {
            requestId,
            fileType,
            startedAt: store.importProcessing.startedAt?.toISOString()
          });
          
          store.importProcessing.isProcessing = false;
          store.importProcessing.requestId = null;
          store.importProcessing.fileType = null;
          store.importProcessing.message = 'タイムアウトによりリセットされました';
          store.importProcessing.startedAt = null;
        }
      }, 10 * 60 * 1000); // 10分 = 600,000ms
    },

    updateImportProgress(message: string) {
      if (store.importProcessing.isProcessing) {
        store.importProcessing.message = message;
      }
    },

    finishImportProcessing() {
      clearImportTimeout();
      store.importProcessing.isProcessing = false;
      store.importProcessing.requestId = null;
      store.importProcessing.fileType = null;
      store.importProcessing.message = '';
      store.importProcessing.startedAt = null;
    },

    requestVersionDiff(params: { pageId: string; fromVersionId: string }) {
      store.pendingVersionDiff = { pageId: params.pageId, fromVersionId: params.fromVersionId };
    },

    clearVersionDiff() {
      store.pendingVersionDiff = null;
    },
  };

  return { store, actions };
}

export type UIStoreInstance = ReturnType<typeof createUIStore>;